// src/screens/customer/biddingActivity/BiddingActivity.jsx
import axios from "axios";
import AppContext from "../../../../context/AppContext";
import React, { useState, useEffect, useContext } from "react";
import BiddingCard from "../../../../components/customer/biddingCard/BiddingCard";
import BiddingDetails from "../../../../subscreen/customer/biddingDetails/BiddingDetails";
import CreateBidding from "../../../../subscreen/customer/createBidding/CreateBidding";
import { Plus, Package, Gavel } from "lucide-react";
import "../../../../assets/css/customer/biddingActivity/BiddingActivity.css";

const BiddingActivity = () => {
  const { baseUrl, loggedInCustomer } = useContext(AppContext);

  // ✅ "posted" is now the default tab
  const [activeTab, setActiveTab] = useState("posted");
  const [selectedBidding, setSelectedBidding] = useState(null);
  const [showCreateBidding, setShowCreateBidding] = useState(false);

  const [placedBids, setPlacedBids] = useState([]);
  const [placedFilter, setPlacedFilter] = useState("all");

  const [postedItems, setPostedItems] = useState([]);
  const [postedFilter, setPostedFilter] = useState("all");

  // ─── API calls ───────────────────────────────────────────
  const fetchPlacedBids = () => {
    const authToken = localStorage.getItem("authToken");
    axios
      .get(`${baseUrl}/customer/bidding/viewmybidplacements`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        console.log("placed bids:", res.data);
        setPlacedBids(res.data.bids || []);
      })
      .catch((err) => console.log(err.response?.data || err.message));
  };

  const fetchPostedItems = () => {
    const authToken = localStorage.getItem("authToken");
    axios
      .get(`${baseUrl}/customer/bidding/viewmybids`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        console.log("posted items:", res.data);
        setPostedItems(res.data.mybids || []);
      })
      .catch((err) => console.log(err.response?.data || err.message));
  };

  useEffect(() => {
    if (!loggedInCustomer) return;
    fetchPlacedBids();
    fetchPostedItems();
  }, [loggedInCustomer]);

  // ─── Placed Bids filters — my_bid_status use karo ────────
  const winningBids = placedBids.filter((b) => b.my_bid_status === "winning");
  const outbidBids  = placedBids.filter((b) => b.my_bid_status === "outbid");
  const endedBids   = placedBids.filter((b) => b.status === "ended");

  const getFilteredPlacedBids = () => {
    switch (placedFilter) {
      case "winning": return winningBids;
      case "outbid":  return outbidBids;
      case "ended":   return endedBids;
      default:        return placedBids;
    }
  };

  // ─── Posted Items filters — pending instead of upcoming ──
  const pendingPosted  = postedItems.filter((b) => b.status === "pending");
  const activePosted   = postedItems.filter((b) => b.status === "active");
  const endedPosted    = postedItems.filter((b) => b.status === "ended");
  const rejectedPosted = postedItems.filter((b) => b.status === "rejected");

  const getFilteredPostedItems = () => {
    switch (postedFilter) {
      case "pending":  return pendingPosted;
      case "active":   return activePosted;
      case "ended":    return endedPosted;
      case "rejected": return rejectedPosted;
      default:         return postedItems;
    }
  };

  return (
    <div className="ba-container">

      {/* ── Header ── */}
      <div className="ba-header">
        <div className="ba-title-section">
          <h1 className="ba-title">Bidding Activity</h1>
          <p className="ba-subtitle">Track and manage all your bidding</p>
        </div>
        <button className="ba-post-btn" onClick={() => setShowCreateBidding(true)}>
          <Plus size={16} strokeWidth={2.5} />
          Post New Bid
        </button>
      </div>

      {/* ── Main Card ── */}
      <div className="ba-main-card">

        {/* Tabs — "Items I Posted" pehle, "Bids I Placed" baad mein */}
        <div className="ba-tabs">
          <button
            className={`ba-tab ${activeTab === "posted" ? "ba-tab-active" : ""}`}
            onClick={() => setActiveTab("posted")}
          >
            <Package size={15} />
            Items I Posted
          </button>
          <button
            className={`ba-tab ${activeTab === "placed" ? "ba-tab-active" : ""}`}
            onClick={() => setActiveTab("placed")}
          >
            <Gavel size={15} />
            Bids I Placed
          </button>
        </div>

        {/* ── TAB 1 — Items I Posted ── */}
        {activeTab === "posted" && (
          <div className="ba-tab-content">
            <div className="ba-filters">
              {[
                { key: "all",      label: "All Items" },
                { key: "pending",  label: "Pending" },
                { key: "active",   label: "Active" },
                { key: "ended",    label: "Ended" },
                { key: "rejected", label: "Rejected" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className={`ba-filter-btn ${postedFilter === key ? "active" : ""}`}
                  onClick={() => setPostedFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {getFilteredPostedItems().length === 0 ? (
              <div className="ba-empty">
                <div className="ba-empty-icon">
                  <Package size={48} strokeWidth={1.2} />
                </div>
                <h3>No items found</h3>
                <p>
                  {postedFilter === "all"
                    ? "You haven't posted any items yet."
                    : `No ${postedFilter} items at the moment.`}
                </p>
                {postedFilter === "all" && (
                  <button className="ba-post-btn" onClick={() => setShowCreateBidding(true)}>
                    <Plus size={16} strokeWidth={2.5} />
                    Post New Item
                  </button>
                )}
              </div>
            ) : (
              <div className="ba-grid">
                {getFilteredPostedItems().map((bidding) => (
                  <BiddingCard
                    key={bidding.id}
                    bidding={bidding}
                    onView={setSelectedBidding}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB 2 — Bids I Placed ── */}
        {activeTab === "placed" && (
          <div className="ba-tab-content">
            <div className="ba-filters">
              {[
                { key: "all",     label: `All Bids` },
                { key: "winning", label: `Winning` },
                { key: "outbid",  label: `Outbid` },
                { key: "ended",   label: `Ended` },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className={`ba-filter-btn ${placedFilter === key ? "active" : ""}`}
                  onClick={() => setPlacedFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {getFilteredPlacedBids().length === 0 ? (
              <div className="ba-empty">
                <div className="ba-empty-icon">🔍</div>
                <h3>No bids found</h3>
                <p>
                  {placedFilter === "all"
                    ? "You haven't placed any bids yet."
                    : `No ${placedFilter} bids at the moment.`}
                </p>
              </div>
            ) : (
              <div className="ba-grid">
                {getFilteredPlacedBids().map((bidding) => (
                  <BiddingCard
                    key={bidding.id}
                    bidding={bidding}
                    onView={setSelectedBidding}
                    isWinning={bidding.my_bid_status === "winning"}
                    userBid={{ amount: bidding.my_bid_amount, status: bidding.my_bid_status }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {selectedBidding && (
        <BiddingDetails
          bidding={selectedBidding}
          isOpen={!!selectedBidding}
          onClose={() => setSelectedBidding(null)}
          mode={activeTab === "posted" ? "posted" : activeTab === "placed" ? "placed" : "browse"}
        />
      )}

      {/* ✅ onClose ke baad fetchPostedItems call hogi — refresh issue fix */}
      {showCreateBidding && (
        <CreateBidding
          isOpen={showCreateBidding}
          onClose={() => {
            setShowCreateBidding(false);
            fetchPostedItems();
          }}
        />
      )}
    </div>
  );
};

export default BiddingActivity;